<template>
  <div>
    <EditModal
      v-if="selectedApplication !== null"
      :application="selectedApplication"
      :onValueChanged="onValueChanged"
      :addAdditionalSectionThree="addAdditionalSectionThree"
      :removeSectionThree="removeSectionThree"
      :addAdditionalSectionFour="addAdditionalSectionFour"
      :removeSectionFour="removeSectionFour"
      :allowImages="false"
      :emailDisclaimerShown="true"
      :showEmailDisclaimer="showEmailDisclaimer"
      :error="canSaveSelectedApplication ? false : 'Missing Information!'"
      :canSaveSelectedApplication="canSaveSelectedApplication"
      :updateApplication="updateApplication"
      :cancelEditing="cancelEditing"
      :submissionDate="originalSelectedApplicationDate"
    />
    <Header :logout="logout" />
    <div
      style="width: 100%; min-width: 100%; max-width: 100%; overflow-x: scroll;"
    >
      <Table
        :applications="applications"
        :editApplication="editApplication"
        :deleteApplication="deleteApplication"
      />
    </div>
  </div>
</template>

<script>
import { auth } from "firebase";
import EditModal from "@/components/Speaker/EditModal";
import Header from "@/components/Speaker/Header";
import Table from "@/components/Speaker/Table";

export default {
  name: "my-applications",
  components: {
    EditModal,
    Header,
    Table
  },
  data: () => {
    return {
      selectedApplication: null
    };
  },
  mounted() {
    this.loadApplications();
  },
  computed: {
    applications() {
      return this.$store.getters["applications/mine"];
    },
    canSaveSelectedApplication() {
      return this.selectedApplication
        ? this.selectedApplication.hasRequiredFields
        : false;
    },
    originalSelectedApplicationDate() {
      return this.selectedApplication
        ? this.applications.find(
            application => application.key === this.selectedApplication.key
          ).submissionDate
        : false;
    }
  },
  methods: {
    async loadApplications() {
      if (this.applications.length > 0) {
        return;
      }
      try {
        let alert = this.$swal({
          title: "Loading your applications...",
          type: "info",
          showConfirmButton: false,
          showCancelButton: false,
          showCloseButton: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          allowEnterKey: false
        });
        await this.$store.dispatch("applications/loadMine");
        alert.close();
      } catch (err) {
        this.$swal({
          title: "Unable to load applications!",
          text: "An unexpected error occurred, please try again later.",
          type: "error"
        });
      }
    },
    async deleteApplication(application) {
      try {
        const result = await this.$swal({
          title: `Delete application submitted on ${application.submissionDate}?`,
          type: "warning",
          text:
            "Deleting an applicaiton is irreversible, please confirm that the selected application is correct and that you want to perform this action.",
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await application.delete();
              await this.loadApplications();
            } catch (err) {
              return false;
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading()
        });
        if (result.value === false) {
          throw new Error();
        } else if (result.dismiss !== "cancel") {
          this.$swal({
            title: "Application deleted!",
            type: "success"
          });
        }
      } catch (err) {
        this.$swal({
          title: "Unable to delete this application!",
          text: "An unexpected error occurred.",
          type: "error"
        });
      }
    },
    onValueChanged(section, name, value) {
      if (section === "root") {
        this.$set(this.selectedApplication, name, value);
      } else {
        this.$set(this.selectedApplication[section], name, value);
      }
    },
    addAdditionalSectionThree() {
      this.selectedApplication.addAdditionalSectionThree();
    },
    removeSectionThree(index) {
      this.selectedApplication.removeAdditionalSectionThree(index - 1);
    },
    addAdditionalSectionFour() {
      this.selectedApplication.addAdditionalSectionFour();
    },
    removeSectionFour(index) {
      this.selectedApplication.removeAdditionalSectionFour(index - 1);
    },
    editApplication(application) {
      this.selectedApplication = application.deepCopy;
    },
    cancelEditing() {
      this.selectedApplication = null;
    },
    async updateApplication() {
      const application = this.selectedApplication;
      this.cancelEditing();
      try {
        const result = await this.$swal({
          title: `Save updates made to application submitted on ${application.submissionDate}?`,
          type: "question",
          text:
            "Updating an applicaiton is irreversible, please confirm that the selected application is correct and that you want to perform this action.",
          showCancelButton: true,
          showLoaderOnConfirm: true,
          preConfirm: async value => {
            try {
              await application.updateSpeakerInformation();
              await this.loadApplications();
            } catch (err) {
              return false;
            }
          },
          allowOutsideClick: () => !this.$swal.isLoading()
        });
        if (result.value === false) {
          throw new Error();
        } else if (result.dismiss !== "cancel") {
          this.$swal({
            title: "Application Updated!",
            type: "success"
          });
        }
      } catch (err) {
        this.$swal({
          title: "Unable to update this application!",
          text: "An unexpected error occurred.",
          type: "error"
        });
      }
    },
    showEmailDisclaimer() {
      const application = this.selectedApplication;
      this.cancelEditing();
      this.$swal({
        title: "Changing Your Email Address",
        text:
          "Changing your email address will revoke access to this application from your current Speakers Bureau account. Applications can only be accessed from accounts that have the exact same email address address as the one specified within the application. If you need or want to change the email address specified within your application, ensure that you type it correctly and that you have access to the new email address. If a Speakers Bureau account does not already exist for the new email address, one will be created for it.",
        type: "info",
        onClose: () => {
          this.editApplication(application);
        }
      });
    },
    async logout() {
      try {
        this.applications = [];
        await this.$store.dispatch("auth/logout");
      } catch (err) {
        // Ignore errors
      } finally {
        this.$router.push("/login");
      }
    }
  }
};
</script>
